import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ViewDidEnter } from '@ionic/angular';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalyticsService } from '../../core/services/analytics.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OrdersService } from '../../core/services/orders.service';
import { OverlayService } from '../../core/services/overlay.service';
import { ValidationService } from '../../core/services/validation.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';

export const OrderUpdateModalPageIdentifier = 'order-update-modal';

export interface OrderUpdateModalPageResult {
  updated: boolean;
}

@Component({
  selector: 'app-order-update-modal',
  templateUrl: './order-update-modal.page.html',
  styleUrls: ['./order-update-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderUpdateModalPage implements ViewDidEnter, OnDestroy {
  @Input()
  public orderId: number;

  @Input()
  public vendorId: number;

  @Input()
  public vendorName: string;

  public specialRequest = '';

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private overlayService: OverlayService,
    private validationService: ValidationService,
    private ordersService: OrdersService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService,
    private modalService: ModalService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  ionViewDidEnter() {
    this.trackPageView();
  }

  public onDismiss({ updated }: OrderUpdateModalPageResult = { updated: false }): void {
    void this.modalService.dismissModal({ id: OrderUpdateModalPageIdentifier, data: { updated } });
  }

  public onUpdateSpecialRequest(specialRequest: string): void {
    this.specialRequest = specialRequest;
  }

  public onSubmit(): void {
    void this.overlayService.showLoading().then(() => {
      this.ordersService
        .submitSpecialRequest(this.orderId, this.vendorId, this.specialRequest)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: () => {
            void this.overlayService.hideLoading().then(() => {
              void this.modalService.showAlert({
                title: this.translateService.instant('SUCCESS') as string,
                message: this.translateService.instant('ORDER_UPDATE_MODAL_PAGE.SUCCESS_MESSAGE') as string,
                buttons: [
                  {
                    text: this.translateService.instant('OK') as string,
                    handler: () => this.onDismiss({ updated: true }),
                  },
                ],
              });

              this.trackSubmitRequestEvent();
            });
          },
          error: (error: HttpErrorResponse) => {
            const errorMessage = this.validationService.getErrorMessage(error);

            this.loggerService.info({
              component: 'OrderUpdateModalPage',
              message: "couldn't submit order change request",
              details: { error, messageShownToUser: errorMessage },
            });

            void this.overlayService
              .hideLoading()
              .then(() => this.modalService.showAlert({ title: this.translateService.instant('ERROR') as string, message: errorMessage }));
          },
        });
    });
  }

  private trackPageView(): void {
    void this.analyticsService.trackView({
      name: this.analyticsConfig.pageName.orderUpdatePage,
      data: {
        [this.analyticsConfig.propertyName.orderId]: this.orderId,
      },
    });
  }

  private trackSubmitRequestEvent(): void {
    void this.analyticsService.trackEvent({
      name: this.analyticsConfig.eventName.orderUpdatePageSubmit,
      data: {
        [this.analyticsConfig.propertyName.orderId]: this.orderId,
      },
    });
  }
}
